import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { StatCard } from './components/StatCard';
import { KpiTimeChart } from './components/KpiTimeChart';
import { ChartDataTable } from './components/ChartDataTable';
import { ViewControls } from './components/ViewControls';
import { SectionExportButton } from './components/SectionExportButton';
import { SectionStatusChip } from './components/SectionStatusChip';
import {
  fmtNum, fmtNumShort, fmtMoney, fmtMoneyShort, fmtPeriodo, calcDelta, prevPeriodo,
} from './components/kpiUtils';
import {
  ArrowRightLeft, ArrowDownLeft, ArrowUpRight, Activity, Scale, Wallet, Info, EyeOff, Loader2,
} from 'lucide-react';

const CIN = '#10b981';   // ingresos
const CEG = '#f43f5e';   // egresos
const CTOT = '#4f46e5';  // movimientos
const CNET = '#6366f1';  // neto
const CINT = '#f59e0b';  // internas (Waya <-> Waya)

const COL_MES = { key: 'mes', label: 'Mes', tipo: 'texto' };
const COL_CONCEPTO = { key: 'concepto', label: 'Concepto', tipo: 'texto' };

const cmpTabla = (items, mesA, mesB, tipo) => ({
  columnas: [COL_CONCEPTO, { key: 'a', label: fmtPeriodo(mesA), tipo }, { key: 'b', label: fmtPeriodo(mesB), tipo },
    { key: 'dif', label: 'Diferencia', tipo }, { key: 'var', label: 'Variación %', tipo: 'pct' }],
  filas: items.map(([concepto, a, b]) => ({
    concepto, a, b, dif: (Number(b) || 0) - (Number(a) || 0),
    var: a ? (((Number(b) || 0) - Number(a)) / Math.abs(Number(a))) * 100 : null,
  })),
});

const KpisTransaccionalidad = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('evolucion');
  const [chartType, setChartType] = useState('barras');
  const [mesUno, setMesUno] = useState('');
  const [mesA, setMesA] = useState('');
  const [mesB, setMesB] = useState('');
  const rootRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get('/kpis/transaccionalidad');
      setData(r.data);
      const ps = (r.data.por_mes || []).map((m) => m.periodo);
      if (ps.length) {
        setMesUno(ps[ps.length - 1]);
        setMesB(ps[ps.length - 1]);
        setMesA(ps.length > 1 ? ps[ps.length - 2] : ps[ps.length - 1]);
      }
    } catch (e) { toast.error('Error al cargar Transaccionalidad'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const esAdmin = !!data?.es_admin;
  const mesesOcultos = data?.meses_ocultos || [];
  const [savingVis, setSavingVis] = useState(false);
  const toggleMesOculto = async (periodo, ocultar) => {
    const next = ocultar
      ? Array.from(new Set([...mesesOcultos, periodo]))
      : mesesOcultos.filter((m) => m !== periodo);
    try {
      setSavingVis(true);
      await api.put('/kpis/transaccionalidad/meses-ocultos', { meses: next });
      toast.success(ocultar ? `Mes ${periodo} ocultado a otros usuarios` : `Mes ${periodo} vuelto a mostrar`);
      await load();
    } catch (e) {
      toast.error('No se pudo actualizar la visibilidad del mes');
    } finally {
      setSavingVis(false);
    }
  };

  const serie = data?.por_mes || [];
  const kpis = data?.kpis || {};
  const periodos = useMemo(() => serie.map((m) => m.periodo), [serie]);
  const byPeriodo = useMemo(() => Object.fromEntries(serie.map((m) => [m.periodo, m])), [serie]);

  const evoMov = useMemo(() => serie.map((m) => ({ label: fmtPeriodo(m.periodo, true), total: m.total })), [serie]);
  const evoCant = useMemo(() => serie.map((m) => ({ label: fmtPeriodo(m.periodo, true), ingresos: m.ingresos_cant, egresos: m.egresos_cant })), [serie]);
  const evoMonto = useMemo(() => serie.map((m) => ({ label: fmtPeriodo(m.periodo, true), ingresos: m.ingresos_monto, egresos: m.egresos_monto })), [serie]);
  const tablaMov = useMemo(() => serie.map((m) => ({ mes: fmtPeriodo(m.periodo), movimientos: m.total })), [serie]);
  const tablaCant = useMemo(() => serie.map((m) => ({ mes: fmtPeriodo(m.periodo), ingresos: m.ingresos_cant, egresos: m.egresos_cant })), [serie]);
  const tablaMonto = useMemo(() => serie.map((m) => ({ mes: fmtPeriodo(m.periodo), ingresos: m.ingresos_monto, egresos: m.egresos_monto, neto: m.monto_neto })), [serie]);

  if (loading) return <div className="p-8 text-slate-500" data-testid="kpis-transaccionalidad-loading">Cargando transaccionalidad…</div>;

  const noData = !serie.length;
  const rowUno = byPeriodo[mesUno];
  const rowUnoPrev = byPeriodo[prevPeriodo(mesUno)];
  const rowA = byPeriodo[mesA];
  const rowB = byPeriodo[mesB];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" ref={rootRef} data-testid="kpis-transaccionalidad">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-indigo-500/10"><ArrowRightLeft className="h-6 w-6 text-indigo-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Transaccionalidad</h1>
            <div className="mt-1.5"><SectionStatusChip seccionKey="kpis-transaccionalidad" /></div>
            <p className="text-sm text-slate-500">Movimientos de cuentas Waya: cantidad por mes e Ingresos vs Egresos {data?.desde ? `· desde ${fmtPeriodo(data.desde)}` : ''}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {esAdmin && periodos.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9" data-testid="transacc-visibilidad-btn">
                  <EyeOff className="h-4 w-4 mr-1.5" />
                  Visibilidad de meses
                  {mesesOcultos.length > 0 && (
                    <Badge className="ml-1.5 bg-amber-100 text-amber-700 hover:bg-amber-100 px-1.5 py-0 text-[10px]">{mesesOcultos.length}</Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-0" data-testid="transacc-visibilidad-panel">
                <div className="px-3 py-2.5 border-b border-slate-100">
                  <p className="text-sm font-semibold text-slate-700">Meses visibles</p>
                  <p className="text-[11px] text-slate-400">Los meses ocultos no se muestran a los usuarios que no son administradores.</p>
                </div>
                <div className="max-h-72 overflow-y-auto py-1">
                  {[...periodos].reverse().map((p) => {
                    const oculto = mesesOcultos.includes(p);
                    return (
                      <div key={p} className="flex items-center justify-between px-3 py-1.5 hover:bg-slate-50" data-testid={`transacc-vis-row-${p}`}>
                        <span className={`text-sm ${oculto ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{fmtPeriodo(p)}</span>
                        <Switch checked={!oculto} disabled={savingVis}
                          onCheckedChange={(checked) => toggleMesOculto(p, !checked)}
                          data-testid={`transacc-vis-toggle-${p}`} />
                      </div>
                    );
                  })}
                </div>
                {savingVis && (
                  <div className="px-3 py-2 text-[11px] text-slate-400 flex items-center gap-1.5 border-t border-slate-100">
                    <Loader2 className="animate-spin" size={12} /> Guardando…
                  </div>
                )}
              </PopoverContent>
            </Popover>
          )}
          <SectionExportButton title="Transaccionalidad" targetRef={rootRef} testId="transaccionalidad-export-btn" />
        </div>
      </div>

      {!data?.disponible ? (
        <Card className="p-8 border-dashed border-2 border-amber-200 bg-amber-50/40 dark:border-amber-900/40 dark:bg-amber-900/10" data-testid="transaccionalidad-no-disponible">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-slate-800 dark:text-slate-200">La base de movimientos no está disponible en este entorno</p>
              <p className="text-sm text-slate-500 mt-1">Los datos de transaccionalidad se leen directamente de la base <span className="font-mono">movimientos</span>, que solo es alcanzable en producción. Una vez publicado el cambio, esta sección mostrará los movimientos de cuentas Waya desde enero.</p>
            </div>
          </div>
        </Card>
      ) : noData ? (
        <Card className="p-10 text-center border-dashed border-2 border-slate-200 dark:border-slate-700" data-testid="transaccionalidad-empty">
          <ArrowRightLeft className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No hay movimientos de cuentas Waya en el rango.</p>
          <p className="text-sm text-slate-400 mt-1">Se muestran los movimientos desde {fmtPeriodo(data?.desde)}.</p>
        </Card>
      ) : (
        <>
          {/* KPIs globales */}
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
            <StatCard label="Total movimientos" value={fmtNum(kpis.total_movimientos)} icon={Activity} accent={CTOT} testId="transaccionalidad-kpi-total" />
            <StatCard label="Ingresos (cantidad)" value={fmtNum(kpis.ingresos_cant)} icon={ArrowDownLeft} accent={CIN} testId="transaccionalidad-kpi-ingresos-cant" />
            <StatCard label="Egresos (cantidad)" value={fmtNum(kpis.egresos_cant)} icon={ArrowUpRight} accent={CEG} testId="transaccionalidad-kpi-egresos-cant" />
            <StatCard label="Monto ingresos" value={fmtMoney(kpis.ingresos_monto)} icon={ArrowDownLeft} accent={CIN} testId="transaccionalidad-kpi-ingresos-monto" />
            <StatCard label="Monto egresos" value={fmtMoney(kpis.egresos_monto)} icon={ArrowUpRight} accent={CEG} testId="transaccionalidad-kpi-egresos-monto" />
            <StatCard label="Monto neto (ing − egr)" value={fmtMoney(kpis.monto_neto)} icon={Scale} accent={CNET} testId="transaccionalidad-kpi-neto" />
            <StatCard label="Internas (cantidad)" value={fmtNum(kpis.internas_cant)} icon={ArrowRightLeft} accent={CINT} testId="transaccionalidad-kpi-internas-cant" />
            <StatCard label="Monto internas" value={fmtMoney(kpis.internas_monto)} icon={ArrowRightLeft} accent={CINT} testId="transaccionalidad-kpi-internas-monto" />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 -mt-4 mb-6 flex items-center gap-1.5" data-testid="transaccionalidad-internas-nota">
            <Info className="w-3.5 h-3.5" /> Las transferencias internas (Waya↔Waya, plata entre cuentas propias) se cuentan aparte y NO se incluyen en ingresos ni egresos.
          </p>

          {/* Controles de vista */}
          <div className="mb-6">
            <ViewControls
              view={view} onView={setView} chartType={chartType} onChartType={setChartType}
              periodos={periodos} mesUno={mesUno} onMesUno={setMesUno}
              mesA={mesA} onMesA={setMesA} mesB={mesB} onMesB={setMesB}
            />
          </div>

          {/* ---------------- EVOLUCIÓN ---------------- */}
          {view === 'evolucion' && (
            <div className="space-y-6" data-testid="transaccionalidad-evolucion">
              <Card className="p-5 lg:p-6" data-testid="transaccionalidad-mov-card">
                <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Cantidad de movimientos por mes</h2>
                <KpiTimeChart data={evoMov} series={[{ key: 'total', name: 'Movimientos', color: CTOT }]}
                  type={chartType} valueFormatter={fmtNum} axisFormatter={fmtNumShort} height={300} showLegend={false} testId="transaccionalidad-mov-chart" />
                <ChartDataTable titulo="Transaccionalidad - Cantidad de movimientos por mes" total
                  columnas={[COL_MES, { key: 'movimientos', label: 'Movimientos', tipo: 'numero' }]}
                  filas={tablaMov} testId="transaccionalidad-mov-tabla" />
              </Card>
              <Card className="p-5 lg:p-6" data-testid="transaccionalidad-cant-card">
                <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Ingresos vs Egresos — cantidad</h2>
                <KpiTimeChart data={evoCant} series={[{ key: 'ingresos', name: 'Ingresos', color: CIN }, { key: 'egresos', name: 'Egresos', color: CEG }]}
                  type={chartType} valueFormatter={fmtNum} axisFormatter={fmtNumShort} height={320} testId="transaccionalidad-cant-chart" />
                <ChartDataTable titulo="Transaccionalidad - Ingresos vs Egresos (cantidad)" total
                  columnas={[COL_MES, { key: 'ingresos', label: 'Ingresos', tipo: 'numero' }, { key: 'egresos', label: 'Egresos', tipo: 'numero' }]}
                  filas={tablaCant} testId="transaccionalidad-cant-tabla" />
              </Card>
              <Card className="p-5 lg:p-6" data-testid="transaccionalidad-monto-card">
                <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Ingresos vs Egresos — montos</h2>
                <KpiTimeChart data={evoMonto} series={[{ key: 'ingresos', name: 'Ingresos', color: CIN }, { key: 'egresos', name: 'Egresos', color: CEG }]}
                  type={chartType} valueFormatter={fmtMoney} axisFormatter={fmtMoneyShort} height={320} testId="transaccionalidad-monto-chart" />
                <ChartDataTable titulo="Transaccionalidad - Ingresos vs Egresos (montos)" total
                  columnas={[COL_MES, { key: 'ingresos', label: 'Ingresos', tipo: 'moneda' }, { key: 'egresos', label: 'Egresos', tipo: 'moneda' }, { key: 'neto', label: 'Neto', tipo: 'moneda' }]}
                  filas={tablaMonto} testId="transaccionalidad-monto-tabla" />
              </Card>
            </div>
          )}

          {/* ---------------- UN MES ---------------- */}
          {view === 'un-mes' && (
            <div className="space-y-6" data-testid="transaccionalidad-un-mes">
              {!rowUno ? (
                <Card className="p-8 text-center text-slate-400">Elegí un mes para ver el detalle.</Card>
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <StatCard label="Movimientos" value={fmtNum(rowUno.total)} icon={Activity} accent={CTOT}
                      delta={calcDelta(rowUno.total, rowUnoPrev?.total)} deltaLabel="vs mes previo" testId="transaccionalidad-um-total" />
                    <StatCard label="Ingresos (cant.)" value={fmtNum(rowUno.ingresos_cant)} icon={ArrowDownLeft} accent={CIN}
                      delta={calcDelta(rowUno.ingresos_cant, rowUnoPrev?.ingresos_cant)} testId="transaccionalidad-um-ingresos-cant" />
                    <StatCard label="Egresos (cant.)" value={fmtNum(rowUno.egresos_cant)} icon={ArrowUpRight} accent={CEG}
                      delta={calcDelta(rowUno.egresos_cant, rowUnoPrev?.egresos_cant)} testId="transaccionalidad-um-egresos-cant" />
                    <StatCard label="Monto ingresos" value={fmtMoney(rowUno.ingresos_monto)} icon={ArrowDownLeft} accent={CIN}
                      delta={calcDelta(rowUno.ingresos_monto, rowUnoPrev?.ingresos_monto)} testId="transaccionalidad-um-ingresos-monto" />
                    <StatCard label="Monto egresos" value={fmtMoney(rowUno.egresos_monto)} icon={ArrowUpRight} accent={CEG}
                      delta={calcDelta(rowUno.egresos_monto, rowUnoPrev?.egresos_monto)} testId="transaccionalidad-um-egresos-monto" />
                    <StatCard label="Monto neto" value={fmtMoney(rowUno.monto_neto)} icon={Scale} accent={CNET} testId="transaccionalidad-um-neto" />
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="p-5 lg:p-6" data-testid="transaccionalidad-um-cant-card">
                      <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Cantidad — {fmtPeriodo(mesUno)}</h2>
                      <KpiTimeChart data={[{ label: 'Ingresos', total: rowUno.ingresos_cant }, { label: 'Egresos', total: rowUno.egresos_cant }]}
                        xKey="label" series={[{ key: 'total', name: 'Movimientos', color: CTOT }]} type="barras" barLayout="vertical"
                        cellColors={[CIN, CEG]} height={160} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="transaccionalidad-um-cant-chart" />
                      <ChartDataTable titulo={`Transaccionalidad - Cantidad ${fmtPeriodo(mesUno)}`} total
                        columnas={[COL_CONCEPTO, { key: 'cantidad', label: 'Cantidad', tipo: 'numero' }]}
                        filas={[{ concepto: 'Ingresos', cantidad: rowUno.ingresos_cant }, { concepto: 'Egresos', cantidad: rowUno.egresos_cant }, { concepto: 'Internas (Waya↔Waya)', cantidad: rowUno.internas_cant }]}
                        testId="transaccionalidad-um-cant-tabla" />
                    </Card>
                    <Card className="p-5 lg:p-6" data-testid="transaccionalidad-um-monto-card">
                      <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Montos — {fmtPeriodo(mesUno)}</h2>
                      <KpiTimeChart data={[{ label: 'Ingresos', total: rowUno.ingresos_monto }, { label: 'Egresos', total: rowUno.egresos_monto }]}
                        xKey="label" series={[{ key: 'total', name: 'Monto', color: CTOT }]} type="barras" barLayout="vertical"
                        cellColors={[CIN, CEG]} height={160} valueFormatter={fmtMoney} axisFormatter={fmtMoneyShort} showLegend={false} testId="transaccionalidad-um-monto-chart" />
                      <ChartDataTable titulo={`Transaccionalidad - Montos ${fmtPeriodo(mesUno)}`}
                        columnas={[COL_CONCEPTO, { key: 'monto', label: 'Monto', tipo: 'moneda' }]}
                        filas={[{ concepto: 'Ingresos', monto: rowUno.ingresos_monto }, { concepto: 'Egresos', monto: rowUno.egresos_monto }, { concepto: 'Neto (ing − egr)', monto: rowUno.monto_neto }, { concepto: 'Internas (aparte)', monto: rowUno.internas_monto }]}
                        testId="transaccionalidad-um-monto-tabla" />
                    </Card>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ---------------- COMPARAR ---------------- */}
          {view === 'comparar' && (
            <div className="space-y-6" data-testid="transaccionalidad-comparar">
              {!rowA || !rowB ? (
                <Card className="p-8 text-center text-slate-400">Elegí dos meses para comparar.</Card>
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <StatCard label={`Movimientos · ${fmtPeriodo(mesB)}`} value={fmtNum(rowB.total)} icon={Activity} accent={CTOT}
                      delta={calcDelta(rowB.total, rowA.total)} deltaLabel={`vs ${fmtPeriodo(mesA)}`} testId="transaccionalidad-cmp-total" />
                    <StatCard label={`Ingresos (cant.) · ${fmtPeriodo(mesB)}`} value={fmtNum(rowB.ingresos_cant)} icon={ArrowDownLeft} accent={CIN}
                      delta={calcDelta(rowB.ingresos_cant, rowA.ingresos_cant)} testId="transaccionalidad-cmp-ingresos-cant" />
                    <StatCard label={`Egresos (cant.) · ${fmtPeriodo(mesB)}`} value={fmtNum(rowB.egresos_cant)} icon={ArrowUpRight} accent={CEG}
                      delta={calcDelta(rowB.egresos_cant, rowA.egresos_cant)} testId="transaccionalidad-cmp-egresos-cant" />
                    <StatCard label={`Monto ingresos · ${fmtPeriodo(mesB)}`} value={fmtMoney(rowB.ingresos_monto)} icon={ArrowDownLeft} accent={CIN}
                      delta={calcDelta(rowB.ingresos_monto, rowA.ingresos_monto)} testId="transaccionalidad-cmp-ingresos-monto" />
                    <StatCard label={`Monto egresos · ${fmtPeriodo(mesB)}`} value={fmtMoney(rowB.egresos_monto)} icon={ArrowUpRight} accent={CEG}
                      delta={calcDelta(rowB.egresos_monto, rowA.egresos_monto)} testId="transaccionalidad-cmp-egresos-monto" />
                    <StatCard label={`Monto neto · ${fmtPeriodo(mesB)}`} value={fmtMoney(rowB.monto_neto)} icon={Scale} accent={CNET}
                      delta={calcDelta(rowB.monto_neto, rowA.monto_neto)} testId="transaccionalidad-cmp-neto" />
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="p-5 lg:p-6" data-testid="transaccionalidad-cmp-cant-card">
                      <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Cantidad — {fmtPeriodo(mesA)} vs {fmtPeriodo(mesB)}</h2>
                      <KpiTimeChart data={[
                        { label: 'Movimientos', [fmtPeriodo(mesA, true)]: rowA.total, [fmtPeriodo(mesB, true)]: rowB.total },
                        { label: 'Ingresos', [fmtPeriodo(mesA, true)]: rowA.ingresos_cant, [fmtPeriodo(mesB, true)]: rowB.ingresos_cant },
                        { label: 'Egresos', [fmtPeriodo(mesA, true)]: rowA.egresos_cant, [fmtPeriodo(mesB, true)]: rowB.egresos_cant },
                      ]} xKey="label" series={[
                        { key: fmtPeriodo(mesA, true), name: fmtPeriodo(mesA), color: '#94a3b8' },
                        { key: fmtPeriodo(mesB, true), name: fmtPeriodo(mesB), color: CTOT },
                      ]} type="barras" height={300} valueFormatter={fmtNum} axisFormatter={fmtNumShort} testId="transaccionalidad-cmp-cant-chart" />
                      <ChartDataTable titulo={`Transaccionalidad - Cantidad ${fmtPeriodo(mesA)} vs ${fmtPeriodo(mesB)}`}
                        {...cmpTabla([['Movimientos', rowA.total, rowB.total], ['Ingresos', rowA.ingresos_cant, rowB.ingresos_cant], ['Egresos', rowA.egresos_cant, rowB.egresos_cant]], mesA, mesB, 'numero')}
                        testId="transaccionalidad-cmp-cant-tabla" />
                    </Card>
                    <Card className="p-5 lg:p-6" data-testid="transaccionalidad-cmp-monto-card">
                      <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Montos — {fmtPeriodo(mesA)} vs {fmtPeriodo(mesB)}</h2>
                      <KpiTimeChart data={[
                        { label: 'Ingresos', [fmtPeriodo(mesA, true)]: rowA.ingresos_monto, [fmtPeriodo(mesB, true)]: rowB.ingresos_monto },
                        { label: 'Egresos', [fmtPeriodo(mesA, true)]: rowA.egresos_monto, [fmtPeriodo(mesB, true)]: rowB.egresos_monto },
                        { label: 'Neto', [fmtPeriodo(mesA, true)]: rowA.monto_neto, [fmtPeriodo(mesB, true)]: rowB.monto_neto },
                      ]} xKey="label" series={[
                        { key: fmtPeriodo(mesA, true), name: fmtPeriodo(mesA), color: '#94a3b8' },
                        { key: fmtPeriodo(mesB, true), name: fmtPeriodo(mesB), color: CNET },
                      ]} type="barras" height={300} valueFormatter={fmtMoney} axisFormatter={fmtMoneyShort} testId="transaccionalidad-cmp-monto-chart" />
                      <ChartDataTable titulo={`Transaccionalidad - Montos ${fmtPeriodo(mesA)} vs ${fmtPeriodo(mesB)}`}
                        {...cmpTabla([['Ingresos', rowA.ingresos_monto, rowB.ingresos_monto], ['Egresos', rowA.egresos_monto, rowB.egresos_monto], ['Neto', rowA.monto_neto, rowB.monto_neto]], mesA, mesB, 'moneda')}
                        testId="transaccionalidad-cmp-monto-tabla" />
                    </Card>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default KpisTransaccionalidad;
